import React, { useEffect, useState } from 'react'
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton,
  useTheme,
  useMediaQuery,
  styled,
} from '@mui/material'
import { ChevronLeft as ChevronLeftIcon, ChevronRight as ChevronRightIcon } from '@mui/icons-material'
import { useTranslationContext } from '../../hooks/translations/translationsProvider'
import UserManagement from './Admin/Users'
import { GRAY_LIGHT_RGB_BORDER, GREEN_MAIN, WHITE } from '../../theme/colors'
import PurchasesManagement from './Admin/Purchases'
import { isMobile } from 'react-device-detect'
import AssociationUserManagement from './Association'
import RedeemManagement from './Admin/Redeem'
import UserBalances from './Admin/Balances'
import IdentityManagement from './Admin/Identity'
import { useUserContext } from '../../hooks/auth/UserProvider'
import { isAdminFinanceUser, isAdminItalianEntityUser, isDevSuperAdminUser, isSuperAdminUser } from '../../helpers/authorizedUser'

interface AdminPAnelProps {
  type: string | null;
}

const ListItemsCustom = styled(ListItem)(({theme}) => ({
  '&.Mui-selected': {
    backgroundColor: `${GREEN_MAIN}07 !important`,
    '&:hover': {
      backgroundColor: `${GREEN_MAIN}20 !important`,
    },
  },
  '&:hover': {
    backgroundColor: `${GREEN_MAIN}20`,
  },
}));

interface ListItemTextCustomProps {
  selected?: boolean;
}

const ListItemTextCustom = styled(ListItemText)<ListItemTextCustomProps>(({ theme, selected }) => ({
  '& .MuiTypography-root': {
    color: GREEN_MAIN,
    fontSize: '0.85rem',
    fontWeight: selected ? 'bold' : 'normal', // qui puoi mettere 'bold' dinamicamente se vuoi con prop esterna
  },
}));

const AdminPanel:React.FC<AdminPAnelProps> = ({type}) => {
  const firstSelection = type === 'admin' ? 'users' : type === 'association' ? 'usersAssociation' : 'user';
  const [selectedSection, setSelectedSection] = useState<string>(firstSelection)
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const { t } = useTranslationContext();
  const { user } = useUserContext();
  const hasFullAdminAccess = isSuperAdminUser(user) || isDevSuperAdminUser(user);
  const canManageUsers = hasFullAdminAccess || isAdminItalianEntityUser(user);
  const canManageFinancial = hasFullAdminAccess || isAdminFinanceUser(user);
  const canManageIdentity = isDevSuperAdminUser(user);

  // Sezioni effettivamente accessibili in base ai permessi, in ordine di priorita'.
  const availableSections = React.useMemo(() => {
    const sections: string[] = [];
    if (canManageUsers) sections.push('users');
    if (canManageFinancial) sections.push('purchases', 'redeem', 'balances');
    if (canManageIdentity) sections.push('identity');
    return sections;
  }, [canManageUsers, canManageFinancial, canManageIdentity]);

  // Sezione da mostrare subito: se quella selezionata non e' accessibile, si usa la prima
  // disponibile. Cosi' si evita il flash su una sezione vuota e il redirect "a rimbalzo".
  const effectiveSection = type === 'admin'
    ? (availableSections.includes(selectedSection) ? selectedSection : (availableSections[0] ?? selectedSection))
    : selectedSection;

  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));

  // Automatically show sidebar on MD and up
  React.useEffect(() => {
    setIsSidebarOpen(isMdUp)
  }, [isMdUp])

  useEffect(() => {
    if (type !== 'admin') {
      return;
    }
    if (availableSections.length === 0) {
      return;
    }
    if (!availableSections.includes(selectedSection)) {
      setSelectedSection(availableSections[0]);
    }
  }, [availableSections, selectedSection, type]);

  return (
    <Box sx={{ display: 'flex', position: 'relative' }}>
      
      {/* MENU */}
      <Box
        sx={{
          marginTop:isMobile ? '-40px' : isMdUp ? '20px' : '-40px',
          width: isMobile ? '200px' : '180px',
          backgroundColor: WHITE,
          borderRight: '1px solid ' + GREEN_MAIN,
          position: {
            xs: 'fixed',
            md: 'relative',
          },
          left: {
            xs: isSidebarOpen ? '0' : (isMobile ? '-200px' : '-180px'),
            md: 0,
          },
          transition: theme.transitions.create(['left'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.standard,
          }),
          zIndex: 2,
          display: 'flex',
          flexDirection: 'column',
        //   height: '100vh', 
          minHeight: '100vh', // Ensures it won't shrink when content is small %
          //borderRight: '1px solid ' + GRAY_LIGHT_RGB_BORDER,
          // borderTop: '1px solid ' + GRAY_LIGHT_RGB_BORDER,
          //borderLeft: {md: '1px solid ' + GRAY_LIGHT_RGB_BORDER}
        }}
      >
        {/* GESTIONE BOTTONE SCORRIMENTO MENU*/}
        {!isMdUp && (
          <IconButton
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            sx={{
              position: 'absolute',
              top: '20px', // Top space from the sidebar
              left: 'calc(100% - 46px)', // Position at the right of the sidebar
              transform: 'translateX(100%)',
              bgcolor: 'background.paper',
              boxShadow: 1,
              borderRadius: '0px',
              border: '1px solid',
              borderColor: 'divider',
              transition: theme.transitions.create(['left'], {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.standard,
              }),
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
          >
            {isSidebarOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </IconButton>
        )}

        <Box sx={{
          padding:isMdUp ? '-20px' : '40px'
        }} />

        {/* MENU  ITEMS */}
        <Box sx={{  overflow: 'auto' }}>
          { type === "admin" ? 
            <List>
              {canManageUsers && (
                <ListItemsCustom
                  selected={effectiveSection === 'users'}
                  onClick={() => setSelectedSection('users')}
                  >
                    <ListItemTextCustom primary={t("Users Informations")} selected={effectiveSection === 'users'}/>
                </ListItemsCustom>
              )}

              {canManageFinancial && (
                <ListItemsCustom
                  selected={effectiveSection === 'purchases'}
                  onClick={() => setSelectedSection('purchases')}
                >
                  <ListItemTextCustom primary={t("Gestione Acquisti")} selected={effectiveSection === 'purchases'} />
                </ListItemsCustom>
              )}

              {canManageFinancial && (
                <ListItemsCustom
                  selected={effectiveSection === 'redeem'}
                  onClick={() => setSelectedSection('redeem')}
                >
                  <ListItemTextCustom primary={t("Gestione Riscatto ASSO")} selected={effectiveSection === 'redeem'} />
                </ListItemsCustom>
              )}

              {canManageFinancial && (
                <ListItemsCustom
                  selected={effectiveSection === 'balances'}
                  onClick={() => setSelectedSection('balances')}
                >
                  <ListItemTextCustom primary={t("Gestione Finanziaria Utenti")} selected={effectiveSection === 'balances'} />
                </ListItemsCustom>
              )}

              {canManageIdentity && (
                <ListItemsCustom
                  selected={effectiveSection === 'identity'}
                  onClick={() => setSelectedSection('identity')}
                >
                  <ListItemTextCustom primary={t("Gestione Identity")} selected={effectiveSection === 'identity'} />
                </ListItemsCustom>
              )}

              {/* <ListItem
                button
                selected={selectedSection === 'settings'}
                onClick={() => setSelectedSection('settings')}
              >
                <ListItemText primary={t('Settings')} />
              </ListItem> */}
            </List> :
            type === "association" ?
            <List>
              <ListItemsCustom
                selected={effectiveSection === 'usersAssociation'}
                onClick={() => setSelectedSection('usersAssociation')}
              >
                <ListItemTextCustom primary={t("Gestione utenti")} selected={effectiveSection === 'usersAssociation'} />
                    
              </ListItemsCustom>
            </List> :
            <></>
            }
        </Box>
      </Box>

      {/* CONTENITORE PRINCIPALE*/}
      <Box
        sx={{
          width: {
            xs: '100%',
            md: '75%',
          },
          padding: 2,
          ml: {
            xs: 0,
            md: 0,
          },
          overflow: 'auto',
        }}
      >
        { type === "admin" ? 
          <>
            {canManageUsers && effectiveSection === 'users' && <UserManagement/>}
            {canManageFinancial && effectiveSection === 'purchases' && <PurchasesManagement/>}
            {canManageFinancial && effectiveSection === 'redeem' && <RedeemManagement/>}
            {canManageFinancial && effectiveSection === 'balances' && <UserBalances />}
            {canManageIdentity && effectiveSection === 'identity' && <IdentityManagement />}
          </> :
          type === "association" ?
          <>
            {effectiveSection === 'usersAssociation' && <AssociationUserManagement/>}
          </> :
          <></>
        }
      </Box>
    </Box>
  )
}

export default AdminPanel
